import React from "react";
import { Archive, CheckCircle2, ExternalLink, FolderPlus, Layers3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/badges/StatusBadge";
import { PlatformBadge } from "@/components/badges/PlatformBadge";
import { ASSET_TYPE_LABELS } from "@/lib/constants";
import { cn, timeAgo } from "@/lib/utils";

const SOURCE_TINT = {
  claude: { bg: "rgba(217,119,6,0.10)", border: "rgba(217,119,6,0.24)", text: "#B45309" },
  codex: { bg: "rgba(22,163,74,0.10)", border: "rgba(22,163,74,0.24)", text: "#15803D" },
  openclaw: { bg: "rgba(143, 224, 74,0.10)", border: "rgba(143, 224, 74,0.24)", text: "#0B7C42" },
  hermes: { bg: "rgba(18, 147, 79,0.08)", border: "rgba(18, 147, 79,0.22)", text: "#0B7C42" },
  n8n: { bg: "rgba(220,38,38,0.10)", border: "rgba(220,38,38,0.24)", text: "#B91C1C" },
  webhook: { bg: "rgba(100,116,139,0.10)", border: "rgba(100,116,139,0.22)", text: "#475569" },
  mcp_server: { bg: "rgba(18, 147, 79,0.10)", border: "rgba(18, 147, 79,0.24)", text: "#0B7C42" },
};

export function ContentInboxCard({
  event,
  selected = false,
  onOpen,
  onPromote,
  onArchive,
  className,
}) {
  const action = event.inboxAction || { label: "Create draft", hint: "Turn this intake item into a content asset.", tone: "draft" };
  const actionClasses = {
    draft: "border-[color:var(--accent-blue)]/40 bg-[color:var(--accent-blue)]/12 text-[color:var(--accent-cyan)]",
    review: "border-amber-200 bg-[color:var(--accent-amber)]/12 text-[color:var(--accent-amber)]",
    context: "border-emerald-200 bg-[color:var(--accent-green)]/12 text-[color:var(--accent-green)]",
    archived: "border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] text-muted-foreground",
  };

  return (
    <article
      data-testid={`content-inbox-card-${event.id}`}
      className={cn(
        "rounded-xl border bg-card p-4 inner-highlight transition hover:-translate-y-0.5 hover:shadow-[0_8px_20px_rgba(18, 22, 20,0.07)]",
        selected && "ring-2 ring-cyan",
        className,
      )}
      style={{ borderColor: selected ? "rgba(143, 224, 74,0.42)" : "var(--border-subtle)" }}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <SourcePill source={event.source} />
            <PlatformBadge platform={event.platform} compact />
            <span className="inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-[0.14em] text-muted-foreground" style={{ borderColor: "var(--border-subtle)" }}>
              <Layers3 className="h-3 w-3" />
              {ASSET_TYPE_LABELS[event.asset_type] || event.asset_type}
            </span>
          </div>
          <h3 className="mt-2 line-clamp-2 text-[14.5px] font-semibold tracking-[-0.005em] text-1">{event.title}</h3>
        </div>
        <div className="flex flex-col items-end gap-1">
          <StatusBadge status={event.status} />
          <span className="text-[11px] text-muted-foreground">{timeAgo(event.received_at)}</span>
        </div>
      </div>

      <p className="mt-3 line-clamp-3 text-sm leading-[1.55] text-muted-foreground">{event.preview}</p>

      <div className="mt-4 min-w-0 rounded-lg border bg-[color:var(--surface-1)] p-3" style={{ borderColor: "var(--border-subtle)" }}>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <span className={cn("inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold", actionClasses[action.tone] || actionClasses.draft)}>
              {action.label}
            </span>
            <p className="mt-2 text-[11.5px] leading-relaxed text-muted-foreground">{action.hint}</p>
          </div>
          <span className="max-w-full truncate font-mono text-[11px] text-muted-foreground">{event.payload?.origin}</span>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t pt-3" style={{ borderColor: "var(--border-subtle)" }}>
        <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={onOpen} data-testid="inbox-open">
          <ExternalLink className="h-3 w-3" /> Open
        </Button>
        <div className="flex flex-wrap items-center gap-2">
          {event.status !== "archived" && (
            <Button
              size="sm"
              onClick={onPromote}
              className="h-8 gap-1.5"
              style={{ backgroundColor: "rgba(22,163,74,0.10)", border: "1px solid rgba(22,163,74,0.24)", color: "#15803D" }}
              data-testid="inbox-create-draft"
            >
              <CheckCircle2 className="h-3 w-3" /> {action.label}
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            className="h-8 gap-1.5"
            onClick={() => {
              onOpen?.();
            }}
            data-testid="inbox-move-to-project"
          >
            <FolderPlus className="h-3 w-3" /> Project
          </Button>
          {event.status !== "archived" && (
            <Button
              size="sm"
              variant="ghost"
              onClick={onArchive}
              className="h-8 gap-1.5 text-muted-foreground"
              data-testid="inbox-archive"
            >
              <Archive className="h-3 w-3" /> Archive
            </Button>
          )}
        </div>
      </div>
    </article>
  );
}

function SourcePill({ source }) {
  const cfg = SOURCE_TINT[source] || SOURCE_TINT.webhook;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium uppercase tracking-[0.08em]"
      style={{ backgroundColor: cfg.bg, borderColor: cfg.border, color: cfg.text }}
      data-testid="content-inbox-source"
    >
      {formatSource(source)}
    </span>
  );
}

function formatSource(source = "") {
  return String(source)
    .replace("_", " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}
